import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

export type ModeSelectorOption<T extends string = string> = {
  value: T;
  label: string;
  icon?: ReactNode;
  description?: string;
  disabled?: boolean;
};

export type ModeSelectorProps<T extends string = string> = {
  value: T;
  options: ModeSelectorOption<T>[];
  onValueChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the group. */
  'aria-label'?: string;
  /** Stable test id for the root group. */
  'data-testid'?: string;
  /** Optional test id resolver for each option. */
  getOptionTestId?: (value: T) => string | undefined;
};

export function ModeSelector<T extends string>({
  value,
  options,
  onValueChange,
  disabled = false,
  className,
  'aria-label': ariaLabel = 'Mode',
  'data-testid': dataTestId = 'mode-selector',
  getOptionTestId,
}: ModeSelectorProps<T>) {
  const activeIndex = useMemo(() => {
    const index = options.findIndex((option) => option.value === value);
    return index >= 0 ? index : 0;
  }, [options, value]);

  if (options.length === 0) {
    return null;
  }

  const rootStyle = {
    '--mode-selector-count': String(options.length),
    '--mode-selector-index': String(activeIndex),
  } as CSSProperties;

  return (
    <div
      className={cn('mode-selector', className)}
      style={rootStyle}
      data-testid={dataTestId}
      data-disabled={disabled ? 'true' : undefined}
    >
      <span className="mode-selector__thumb" aria-hidden />

      <ToggleGroup
        type="single"
        value={value}
        onValueChange={(next) => {
          if (!next || next === value) {
            return;
          }

          const option = options.find((item) => item.value === next);
          if (!option || option.disabled || disabled) {
            return;
          }

          onValueChange(next as T);
        }}
        disabled={disabled}
        spacing={0}
        aria-label={ariaLabel}
        className="mode-selector__group"
        style={{
          gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
        }}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            disabled={disabled || option.disabled}
            aria-label={option.label}
            title={option.description}
            data-testid={getOptionTestId?.(option.value)}
            className="mode-selector__item"
          >
            {option.icon ? (
              <span className="mode-selector__item-icon" aria-hidden>
                {option.icon}
              </span>
            ) : null}
            <span className="mode-selector__item-label">{option.label}</span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
